import { z } from 'zod';
import { Language } from '../enums/languages';

const ENGLISH_TRANSLATION_ERROR = 'English translation is missing';

export const getTranslationValidation = () => {
  const obj = {
    [Language.EN]: z.string().min(1, ENGLISH_TRANSLATION_ERROR),
    [Language.DE]: z.string(),
    [Language.NL]: z.string(),
    [Language.CN]: z.string(),
    [Language.RU]: z.string(),
    [Language.IT]: z.string(),
    [Language.FR]: z.string(),
    [Language.JA]: z.string(),
    [Language.ES]: z.string(),
  };

  return z
    .object(obj)
    .partial()
    .refine(
      (data) => {
        return data[Language.EN] !== undefined;
      },
      { message: ENGLISH_TRANSLATION_ERROR, path: [Language.EN] }
    );
};

export const getTranslationValidationOptional = () => {
  const obj = {
    [Language.EN]: z.string(),
    [Language.DE]: z.string(),
    [Language.NL]: z.string(),
    [Language.CN]: z.string(),
    [Language.RU]: z.string(),
    [Language.IT]: z.string(),
    [Language.FR]: z.string(),
    [Language.JA]: z.string(),
    [Language.ES]: z.string(),
  };

  const validation = z.object(obj).partial();

  return validation;
};

export const getTranslationValidationWithLimit = (limit: number = 255, maxErrorMessage: string = 'validation_name_too_long') => {
  const obj = {
    [Language.EN]: z.string().min(1, ENGLISH_TRANSLATION_ERROR).max(limit, maxErrorMessage),
    [Language.DE]: z.string().max(limit, maxErrorMessage),
    [Language.NL]: z.string().max(limit, maxErrorMessage),
    [Language.CN]: z.string().max(limit, maxErrorMessage),
    [Language.RU]: z.string().max(limit, maxErrorMessage),
    [Language.IT]: z.string().max(limit, maxErrorMessage),
    [Language.FR]: z.string().max(limit, maxErrorMessage),
    [Language.JA]: z.string().max(limit, maxErrorMessage),
    [Language.ES]: z.string().max(limit, maxErrorMessage),
  };

  return z
    .object(obj)
    .partial()
    .refine(
      (data) => {
        return data[Language.EN] !== undefined;
      },
      { message: ENGLISH_TRANSLATION_ERROR, path: [Language.EN] }
    );
};

/**
 * Configurable translation validation helper that works with any locale set
 * @param locales - The locale enum/object to validate against
 * @param options - Configuration options
 * @param options.limit - Maximum character limit (default: 255)
 * @param options.maxErrorMessage - Error message for max length validation (default: 'validation_name_too_long')
 * @param options.requireNonEmptyEn - Whether to require non-empty EN translation with trim check (default: false)
 * @param options.requiredTranslationKey - The key for the required translation (default: 'EN')
 * @returns Zod schema for translation validation
 */
export const getTranslationValidationConfigurable = <
  T extends Record<string, string>
>(
  locales: T,
  options: {
    limit?: number;
    maxErrorMessage?: string;
    requireNonEmptyEn?: boolean;
    requiredTranslationKey?: keyof T;
  } = {}
) => {
  const {
    limit = 255,
    maxErrorMessage = 'validation_name_too_long',
    requireNonEmptyEn = false,
    requiredTranslationKey = 'EN' as keyof T,
  } = options;

  // Create validation object for each locale
  const validationObj = {} as Record<
    T[keyof T],
    z.ZodOptional<z.ZodString>
  >;

  Object.keys(locales).forEach(localeKey => {
    const isRequired = localeKey === requiredTranslationKey;
    let validator = z.string().max(limit, maxErrorMessage);

    if (isRequired) {
      validator = validator.min(1, ENGLISH_TRANSLATION_ERROR);
    }

    validationObj[locales[localeKey as keyof T]] = validator.optional();
  });

  let schema = z.object(validationObj).partial();

  // The locale keys are only known through the generic `locales` map, so the
  // refinement reads the parsed value through a plain locale-keyed view.
  const requiredLocale = locales[requiredTranslationKey];
  const readRequired = (data: unknown): string | undefined => {
    if (!data || typeof data !== 'object') {
      return undefined;
    }

    const value = (data as Record<string, unknown>)[requiredLocale];

    return typeof value === 'string' ? value : undefined;
  };

  // Apply refinements
  if (requireNonEmptyEn) {
    schema = schema.refine(
      (data) => {
        const requiredValue = readRequired(data);
        return !!requiredValue && requiredValue.trim() !== '';
      },
      {
        message: 'Default translation (EN) for field must have non-empty text',
        path: [requiredLocale],
      }
    );
  } else {
    schema = schema.refine((data) => readRequired(data) !== undefined, {
      message: ENGLISH_TRANSLATION_ERROR,
      path: [requiredLocale],
    });
  }

  return schema;
};
